import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Card, Typography } from "@mui/material";
import { HugeiconsIcon, IconSvgElement } from "@hugeicons/react";
import {
  ArrowUpRight01Icon,
  Copy01Icon,
  Analytics01Icon,
  Store01Icon,
  UserGroupIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { Stack } from "components/layout/reusableLayouts";
import CardHeader from "components/common/CardHeader";
import CustomSkeleton from "components/common/CustomSkeleton";
import { astradaMocksEnabled } from "components/admin/linkedAccounts/mockAstradaData";
import useSpendingData from "hooks/useSpendingData";
import { TimeFrameEnum } from "enums/AnalyticsTimeFrame";
import { formatCashFull } from "utils/formatCash";
import { Insight, InsightTone, MOCK_INSIGHTS } from "./mockInsights";

export interface ProgramInsightsPanelProps {
  timeFrame?: TimeFrameEnum;
}

const iconFor: Record<Insight["icon"], IconSvgElement> = {
  trend: ArrowUpRight01Icon,
  chart: Analytics01Icon,
  duplicate: Copy01Icon,
  user: UserGroupIcon,
  merchant: Store01Icon,
};

const toneBackground: Record<InsightTone, string> = {
  brand: "var(--color-bg-brand-subtle)",
  warning: "var(--color-bg-warning)",
  info: "var(--color-bg-info)",
};

const toneForeground: Record<InsightTone, string> = {
  brand: "var(--color-text-brand)",
  warning: "var(--color-text-warning)",
  info: "var(--color-text-info-strong)",
};

const InsightRow: React.FC<{ insight: Insight; onOpen?: () => void }> = ({
  insight,
  onOpen,
}) => {
  const interactive = Boolean(onOpen);

  return (
    <Box
      component={interactive ? "button" : "div"}
      type={interactive ? "button" : undefined}
      onClick={onOpen}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: "var(--space-4)",
        width: "100%",
        padding: "var(--space-3)",
        borderRadius: "var(--radius-md)",
        font: "inherit",
        color: "inherit",
        textAlign: "left",
        background: "none",
        border: "none",
        cursor: interactive ? "pointer" : "default",
        transition: "background-color 0.15s ease",
        ...(interactive && {
          "&:hover": { backgroundColor: "var(--color-bg-elevated)" },
          "&:focus-visible": {
            outline: "var(--border-width-2) solid var(--color-border-focus)",
            outlineOffset: "-2px",
          },
        }),
      }}
    >
      <Box
        aria-hidden
        sx={{
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 32,
          height: 32,
          borderRadius: "var(--radius-sm)",
          backgroundColor: toneBackground[insight.tone],
          color: toneForeground[insight.tone],
        }}
      >
        <HugeiconsIcon
          icon={iconFor[insight.icon]}
          size={16}
          color="currentColor"
        />
      </Box>

      <Stack gap="var(--space-1)" sx={{ minWidth: 0 }}>
        <Typography variant="h6" noWrap>
          {insight.title}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "var(--color-text-tertiary)" }}
          noWrap
        >
          {insight.detail}
        </Typography>
      </Stack>
    </Box>
  );
};

/**
 * The handful of things most worth acting on.
 *
 * Two rows are computed from `pieChartSpending`; the other three are mocked and
 * come from `mockInsights`, which documents why each one has no backing. The
 * mocks show only while the mock flag is on (`astradaMocksEnabled`, never in a
 * production build), so a real company never sees fabricated activity. They
 * are merged rather than kept separate so the panel reads as one list, and so
 * swapping a mock for a real row is a one-line change here.
 */
const ProgramInsightsPanel: React.FC<ProgramInsightsPanelProps> = ({
  timeFrame = TimeFrameEnum.LAST_30_DAYS,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const showMocks = astradaMocksEnabled();

  const {
    pieData: byUser,
    currency,
    loading: userLoading,
  } = useSpendingData({
    type: "pie",
    groupKey: "userName",
    filters: { timeFrame },
  });

  const { pieData: byMerchant, loading: merchantLoading } = useSpendingData({
    type: "pie",
    groupKey: "merchant",
    filters: { timeFrame },
  });

  const insights: Insight[] = useMemo(() => {
    const top = (rows: { name: string; value: number }[]) =>
      rows.reduce<{ name: string; value: number } | null>(
        (best, row) => (!best || row.value > best.value ? row : best),
        null,
      );

    const topUser = top(byUser);
    const topMerchant = top(byMerchant);
    const money = (value: number) => formatCashFull(value, currency || "usd");

    const real: Insight[] = [];
    if (topUser) {
      real.push({
        id: "top-user",
        icon: "user",
        tone: "info",
        title: t("Most active card holder"),
        detail: `${topUser.name} · ${money(topUser.value)}`,
        href: "/admin/analytics",
      });
    }
    if (topMerchant) {
      real.push({
        id: "top-merchant",
        icon: "merchant",
        tone: "brand",
        title: t("Top merchant"),
        detail: `${topMerchant.name} · ${money(topMerchant.value)}`,
        href: "/admin/analytics",
      });
    }

    return [...(showMocks ? MOCK_INSIGHTS : []), ...real];
  }, [byUser, byMerchant, currency, t, showMocks]);

  // Skeleton only while nothing is on screen, so rows that have arrived stay put.
  const loading = (userLoading || merchantLoading) && insights.length === 0;

  return (
    <Card
      variant="outlined"
      sx={{
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        padding: "var(--space-5)",
        gap: "var(--space-5)",
        height: "100%",
      }}
    >
      <CardHeader
        title={t("Program insights")}
        subtitle={t("Top things to act on")}
      />

      {/* Scrolls rather than growing, so this panel matches the height of the
          two beside it however many rows there are. */}
      <Stack
        gap="var(--space-2)"
        sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}
      >
        {loading && <CustomSkeleton variant="list" rows={3} />}
        {!loading && insights.length === 0 && (
          <Typography
            variant="body1"
            sx={{
              color: "var(--color-text-tertiary)",
              textAlign: "center",
              padding: "var(--space-7) 0",
            }}
          >
            {t("No insights yet")}
          </Typography>
        )}
        {insights.map((insight) => (
          <InsightRow
            key={insight.id}
            insight={insight}
            onOpen={
              insight.href ? () => navigate(insight.href as string) : undefined
            }
          />
        ))}
      </Stack>
    </Card>
  );
};

export default ProgramInsightsPanel;
